import React from 'react';
import { Text } from '@hangar/web-components/core/Text';
import { ArrowDownwardOutlined } from '@hangar/react-icons/core/interaction/ArrowDownwardOutlined';
import { ArrowUpwardOutlined } from '@hangar/react-icons/core/interaction/ArrowUpwardOutlined';
import { SortOutlined } from '@hangar/react-icons/core/interaction/SortOutlined';
import { SortByField, SortOrder } from '../../services/api/flightChanges';
import * as S from './FlightChangesCases.styles';

export interface FlightChangesCasesSortableColumn {
    sortBy: SortByField;
    labelKey: string;
}

interface FlightChangesTableHeadProps {
    sortBy: SortByField;
    sortOrder: SortOrder;
    onSortRequest?: (property: SortByField) => void;
    columns: FlightChangesCasesSortableColumn[];
    t: (key: string) => string;
}

const FlightChangesTableHead = ({
    sortBy,
    sortOrder,
    onSortRequest,
    columns,
    t,
}: FlightChangesTableHeadProps) => (
    <S.DataTableHead>
        <S.DataTableRow>
            {columns.map(({ sortBy: columnSortBy, labelKey }) => {
                const isActive = sortBy === columnSortBy;
                let ariaSort: 'ascending' | 'descending' | 'none' = 'none';
                if (isActive) {
                    ariaSort = sortOrder === 'asc' ? 'ascending' : 'descending';
                }
                return (
                    <S.DataTableHeaderCell
                        key={columnSortBy}
                        scope="col"
                        aria-sort={ariaSort}
                        data-testid={`sort-header-${columnSortBy}`}
                    >
                        <S.SortHeaderButton
                            type="button"
                            onClick={onSortRequest ? () => onSortRequest(columnSortBy) : undefined}
                            disabled={!onSortRequest}
                        >
                            <Text variant="label-md" bold>
                                {t(labelKey)}
                            </Text>
                            {(() => {
                                if (!isActive) {
                                    return (
                                        <S.SortIconInactive>
                                            <SortOutlined aria-hidden />
                                        </S.SortIconInactive>
                                    );
                                }
                                return sortOrder === 'asc' ? (
                                    <ArrowUpwardOutlined aria-hidden />
                                ) : (
                                    <ArrowDownwardOutlined aria-hidden />
                                );
                            })()}
                        </S.SortHeaderButton>
                    </S.DataTableHeaderCell>
                );
            })}
            <S.DataTableHeaderCell style={{ width: 56 }} />
        </S.DataTableRow>
    </S.DataTableHead>
);

export interface FlightChangesCasesTableFrameProps {
    tableAriaLabel: string;
    sortBy: SortByField;
    sortOrder: SortOrder;
    onSortRequest?: (property: SortByField) => void;
    columns: FlightChangesCasesSortableColumn[];
    t: (key: string) => string;
    children: React.ReactNode;
}

export const FlightChangesCasesTableFrame = ({
    tableAriaLabel,
    sortBy,
    sortOrder,
    onSortRequest,
    columns,
    t,
    children,
}: FlightChangesCasesTableFrameProps) => (
    <S.DataTableContainer>
        <S.DataTable aria-label={tableAriaLabel} data-testid="flight-table">
            <FlightChangesTableHead
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortRequest={onSortRequest}
                columns={columns}
                t={t}
            />
            <S.DataTableBody>{children}</S.DataTableBody>
        </S.DataTable>
    </S.DataTableContainer>
);
